import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock, Users, Sparkles } from "lucide-react";

export default function Invite() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user, initialized, login, register, guest, refreshMe } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [joining, setJoining] = useState(false);

  // forms
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ email: "", username: "", password: "" });
  const [guestName, setGuestName] = useState("");

  // Fetch public preview (no auth required)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/rooms/invite/${code.toUpperCase()}`);
        if (active) setPreview(data);
      } catch (e) {
        if (active) setErr(formatError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [code]);

  const doJoin = async () => {
    setJoining(true);
    try {
      const { data } = await api.post(`/rooms/join/${code.toUpperCase()}`);
      toast.success(`Joined: ${data.name}`);
      nav(`/room/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setJoining(false);
    }
  };

  // If already authed when opening the link, auto-join.
  useEffect(() => {
    if (initialized && user && preview && !preview.is_full) {
      doJoin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, user, preview?.id]);

  const handleAuth = (fn) => async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      await fn();
      await refreshMe();
      // useEffect above will auto-join once `user` is set
    } catch (err) {
      toast.error(formatError(err));
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 font-display tracking-wider animate-pulse">
        unlocking invite…
      </div>
    );
  }

  if (err || !preview) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass rounded-3xl p-10 max-w-md text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h1 className="font-display text-2xl font-bold mb-2">This door is closed.</h1>
          <p className="text-zinc-400 text-sm">{err || "Invalid or expired invite."}</p>
          <button onClick={() => nav("/")} className="ghost-btn mt-6">Go home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain flex items-center justify-center p-4">
      <div className="max-w-lg w-full glass rounded-3xl p-8 shadow-[0_0_80px_rgba(232,28,255,0.15)]" data-testid="invite-card">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/5 text-xs tracking-widest uppercase text-fuchsia-300 mb-5">
          <Sparkles className="w-3.5 h-3.5" /> You were invited
        </div>

        <h1 className="font-display text-4xl font-black tracking-tighter leading-tight">
          {preview.owner ? (
            <>
              <span className="text-fuchsia-400">@{preview.owner.username}</span> saved you a seat in
            </>
          ) : (
            <>You&apos;re invited to</>
          )}{" "}
          <span className="block mt-1 bg-gradient-to-r from-[#e81cff] via-[#ff2a5f] to-[#00f0ff] bg-clip-text text-transparent">
            {preview.name}
          </span>
        </h1>
        {preview.description && (
          <p className="text-zinc-400 mt-4 text-sm">{preview.description}</p>
        )}

        <div className="mt-6 flex items-center justify-between text-xs text-zinc-400 border-t border-white/5 pt-4">
          <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-fuchsia-400" /> {preview.filled}/{preview.max_participants} seats</span>
          <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Mini room · Invite-only</span>
        </div>

        {preview.is_full ? (
          <div className="mt-6 rounded-2xl border border-rose-500/20 bg-rose-500/5 p-4 text-center">
            <div className="font-display font-bold text-rose-400">The room is full.</div>
            <div className="text-xs text-zinc-400 mt-1">Mini rooms are capped at 4 people.</div>
          </div>
        ) : initialized && user ? (
          <button onClick={doJoin} disabled={joining} data-testid="invite-accept-btn" className="neon-btn w-full mt-6">
            {joining ? "Slipping in…" : `Enter as ${user.username}`}
          </button>
        ) : (
          <div className="mt-6">
            <div className="text-xs tracking-widest uppercase text-zinc-500 mb-3">First, who are you?</div>
            <Tabs defaultValue="guest" className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-black/40 p-1 rounded-full border border-white/5">
                <TabsTrigger value="guest" className="rounded-full text-sm data-[state=active]:bg-[#e81cff] data-[state=active]:text-white">Guest</TabsTrigger>
                <TabsTrigger value="login" className="rounded-full text-sm data-[state=active]:bg-[#e81cff] data-[state=active]:text-white">Sign in</TabsTrigger>
                <TabsTrigger value="register" className="rounded-full text-sm data-[state=active]:bg-[#e81cff] data-[state=active]:text-white">Create</TabsTrigger>
              </TabsList>
              <TabsContent value="guest" className="mt-5">
                <form onSubmit={handleAuth(() => guest(guestName))} className="space-y-3">
                  <input data-testid="invite-guest-name" required minLength={2} placeholder="what do we call you?" className="text-input" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
                  <button data-testid="invite-guest-submit" disabled={joining} className="neon-btn w-full">{joining ? "Entering…" : "Slip in as guest"}</button>
                </form>
              </TabsContent>
              <TabsContent value="login" className="mt-5">
                <form onSubmit={handleAuth(() => login(loginForm.email, loginForm.password))} className="space-y-3">
                  <input required type="email" placeholder="email" className="text-input" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                  <input required type="password" placeholder="password" className="text-input" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                  <button disabled={joining} className="neon-btn w-full">{joining ? "Entering…" : "Sign in & join"}</button>
                </form>
              </TabsContent>
              <TabsContent value="register" className="mt-5">
                <form onSubmit={handleAuth(() => register(regForm.email, regForm.password, regForm.username))} className="space-y-3">
                  <input required placeholder="pick a handle" className="text-input" value={regForm.username} onChange={(e) => setRegForm({ ...regForm, username: e.target.value })} />
                  <input required type="email" placeholder="email" className="text-input" value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
                  <input required type="password" minLength={6} placeholder="password (6+)" className="text-input" value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
                  <button disabled={joining} className="neon-btn w-full">{joining ? "Creating…" : "Create & join"}</button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        )}

        <div className="mt-6 text-[10px] tracking-[0.25em] uppercase text-zinc-600 text-center">
          Invite code · {preview.invite_code}
        </div>
      </div>
    </div>
  );
}
